import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { AutoComplete } from 'primeng/autocomplete';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { TranslocoService } from '@jsverse/transloco';
import { AutocompleteChip } from '@app/models/autocomplete-chip.model';
import { ToastServiceManager } from '@services/toast-service-manager.service';
import { ConfirmationService } from 'primeng/api';
import { DialogService } from 'primeng/dynamicdialog';
import { finalize, Observable, take } from 'rxjs';
import { Address } from '@address-models/address.model';
import { PremiseHazardType } from '@premise-hazard-type-models/premise-hazard-type.model';
import { PremiseHazardInputModel } from '@premise-hazard-models/premise-hazard-input.model';
import { PremiseHazardRecordInputModel } from '@premise-hazard-models/premise-hazard-record-input.model';
import { PremiseHazard } from '@premise-hazard-models/premise-hazard.model';
import { ResponseItem } from '@models/response-item.model';
import { PremiseHazardCommandsRestService } from '@services/rest/premise-hazard-command-rest.service';
import { TemplateConstants } from '@constants/template-constants';
import { PremiseHazardItemGeocodingService } from '../premise-hazard-item-geocoding/premise-hazard-item-geocoding.service';
import { PremiseHazardItemContactsComponent } from '../premise-hazard-item-contacts/premise-hazard-item-contacts.component';
import { ContactInputModel } from '@premise-hazard-models/contact-input.model';
import { PremiseHazardPriorityType } from '@app/models/premise-hazard/premise-hazard-priority-type.model';
import { VerifyResult } from '@geocode-models/verify-result.model';
import { Geocoder } from '@app/models/geocode/geocoder.model';
import { GeocodingRestService } from '@app/services/rest/geocoding-rest.service';
import { LoadingDialogComponent, LoadingDialogData } from '@app/shared/loading-dialog/loading-dialog.component';
import { BaseItemComponent } from '@app/shared/base-item.component';
import { QuickEditEvent } from '@app/models/quick-edit-event.model';

@Component({
  selector: 'app-premise-hazard-item',
  templateUrl: './premise-hazard-item.component.html',
  styleUrls: ['./premise-hazard-item.component.scss'],
  standalone: false,
})
export class PremiseHazardItemComponent extends BaseItemComponent<PremiseHazard> implements OnInit {
  @ViewChild(PremiseHazardItemContactsComponent) contactsComponent!: PremiseHazardItemContactsComponent;
  @ViewChild('PremiseHazardTypeAutocomplete') PremiseHazardTypeAutocomplete!: AutoComplete;
  @ViewChild('AddressAutocomplete') AddressAutocomplete!: ElementRef<any>;

  @Input() SelectedPremiseHazard?: PremiseHazard;
  @Input() PremiseHazardTypes: PremiseHazardType[] = new Array<PremiseHazardType>();
  @Input() Addresses: Address[] = new Array<Address>();
  @Input() IsQuickEdit: boolean = false;

  @Output() quickEditEvent: EventEmitter<QuickEditEvent> = new EventEmitter<QuickEditEvent>();

  public PremiseHazardForm!: FormGroup;
  public IsUpdating: boolean = false;
  public minDate: Date;
  public isCreatingAddress: boolean = false;
  public selectedAddressCandidate: VerifyResult | undefined;
  public PremiseHazardTypeOptions: AutocompleteChip[] = [];
  public AddressOptions: AutocompleteChip[] = [];
  public PremiseHazardTypeFilteredOptions: AutocompleteChip[] = [];
  public AddressFilteredOptions: AutocompleteChip[] = [];
  public SelectedTypeOption: AutocompleteChip | null = null;
  public SelectedAddressOption: AutocompleteChip | null = null;
  public IsAutocompleteLoading: boolean = true;
  public EmptyId = TemplateConstants.EmptyId;
  public PURGE_TYPES = TemplateConstants.PurgeTypes;
  public PRIORITY_TYPES = TemplateConstants.PriorityTypes;

  private formIncompleteError!: string;
  private doNotCloseMessage!: string;
  private savingPremiseHazardModalTitle!: string;
  private saveSuccessMessage!: string;
  private updateSuccessMessage!: string;

  constructor(
    private readonly commandService: PremiseHazardCommandsRestService,
    toastServiceManager: ToastServiceManager,
    private readonly confirmationService: ConfirmationService,
    private readonly dialogService: DialogService,
    private readonly cdr: ChangeDetectorRef,
    private readonly transloco: TranslocoService,
    private readonly geocodingService: GeocodingRestService,
    private readonly geocodingDialogs: PremiseHazardItemGeocodingService,
  ) {
    super(toastServiceManager);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    this.minDate = today;
  }

  ngOnInit(): void {
    this.IsCreating = !this.SelectedPremiseHazard;
    this.IsUpdating = !this.IsCreating;
    this.createPremiseHazardForm();
    this.cdr.detectChanges();
    this.setupAutocompletes();
    this.loadTranslations();
  }

  public override cancelClicked(): void {
    if (this.IsQuickEdit) {
      this.quickEditEvent.emit({ itemCreated: false });
      return;
    }
    super.cancelClicked();
  }

  public onSubmit(): void {
    if (!this.beginSubmit(this.PremiseHazardForm, this.formIncompleteError)) {
      return;
    }
    let premiseHazard = this.PremiseHazardForm.value;

    const addressId: string = this.PremiseHazardForm.controls['addressId'].value;
    const typeId: string = this.PremiseHazardForm.controls['premiseHazardTypeId'].value;

    const premiseHazardInput: PremiseHazardInputModel = {
      AddressId: addressId,
      PremiseHazardTypeId: typeId,
      AreaId: undefined,
      RecordTitle: premiseHazard.recordTitle,
      OwnerName: premiseHazard.ownerName,
      OwnerPhone: premiseHazard.ownerPhone,
      OwnerAddress: premiseHazard.ownerAddress,
      Comments: premiseHazard.comments,
      AuthorizingPersonnel: premiseHazard.authorizingPersonnel,
      PurgeDate: premiseHazard.purgeDate,
      PurgeType: premiseHazard.purgeType,
      IsTemporary: premiseHazard.isTemporary,
      IsReviewRequired: premiseHazard.isReviewRequired,
      Priority: premiseHazard.priority,
      Contacts: (this.contactsComponent?.getContacts() || []).map((contact) => {
        return {
          name: contact.name,
          id: contact.id,
          devices: contact.devices.map((device) => {
            return {
              id: device.id,
              address: device.address,
              deviceType: device.deviceType,
            };
          }),
        };
      }),
    };
    const inputModel: PremiseHazardRecordInputModel = {
      PremiseHazard: premiseHazardInput,
      Address: undefined,
      Type: undefined,
    };

    if (this.isCreatingAddress && this.selectedAddressCandidate && this.selectedAddressCandidate.matchLocation) {
      inputModel.Address = {
        commonPlace: this.selectedAddressCandidate.matchLocation.commonplace,
        apartmentNumber: this.selectedAddressCandidate.matchLocation.apartment,
        houseAddress: this.selectedAddressCandidate.matchLocation.houseAddress,
        buildingNumber: this.selectedAddressCandidate.matchLocation.building,
        city: this.selectedAddressCandidate.matchLocation.city,
        description: this.selectedAddressCandidate.matchLocation.description,
        floor: this.selectedAddressCandidate.matchLocation.floor,
        highCrossStreet: this.selectedAddressCandidate.matchLocation.highCrossStreet,
        intersection: this.selectedAddressCandidate.matchLocation.intersection,
        latitude: this.selectedAddressCandidate.matchLocation.y!,
        longitude: this.selectedAddressCandidate.matchLocation.x!,
        lowCrossStreet: this.selectedAddressCandidate.matchLocation.lowCrossStreet,
        postalCode: this.selectedAddressCandidate.matchLocation.zipCode,
        routeLatitude: this.selectedAddressCandidate.matchLocation.routeLatitude,
        routeLongitude: this.selectedAddressCandidate.matchLocation.routeLongitude,
        state: this.selectedAddressCandidate.matchLocation.state,
        streetType: undefined,
        subdivision: this.selectedAddressCandidate.matchLocation.subdivision,
        isVerified: true,
      };
      if (this.isCreatingAddress) {
        if (this.selectedAddressCandidate?.sourceGeocoder === Geocoder.esriOnline) {
          this.geocodingService
            .verifyForStorage(
              {
                address: this.selectedAddressCandidate.matchLocation!.houseAddress,
                city: this.selectedAddressCandidate.matchLocation!.city,
                zipCode: this.selectedAddressCandidate.matchLocation!.zipCode,
                building: this.selectedAddressCandidate.matchLocation!.building,
                apartment: this.selectedAddressCandidate.matchLocation!.apartment,
                floor: this.selectedAddressCandidate.matchLocation!.floor,
                subdivision: this.selectedAddressCandidate.matchLocation!.subdivision,
                locationName: this.selectedAddressCandidate.matchLocation!.commonplace,
              },
              this.selectedAddressCandidate!.sourceGeocoder!,
            )
            .pipe(take(1))
            .subscribe((response: VerifyResult[]) => {
              inputModel.Address!.isVerified = true;
              this.submitPremiseHazard(inputModel);
            });
        } else {
          inputModel.Address!.isVerified = true;
          this.submitPremiseHazard(inputModel);
        }
      }
    } else {
      this.submitPremiseHazard(inputModel);
    }
  }

  public onPurgeDateChanged(event: any): void {
    this.PremiseHazardForm.controls['purgeDate'].setValue(event);
    this.cdr.detectChanges();
  }

  public openSuggestModal(): void {
    this.geocodingDialogs.openSuggestPicker().subscribe((candidate) => this.onGeocodeAddressSelected(candidate));
  }

  public openGeocodeModal(): void {
    this.geocodingDialogs.openAdvancedGeocode().subscribe((candidate) => this.onGeocodeAddressSelected(candidate));
  }

  public onGeocodeAddressSelected(addressCandidate: any): void {
    this.isCreatingAddress = true;
    this.selectedAddressCandidate = addressCandidate;
    this.PremiseHazardForm.controls['addressId'].clearValidators();
    this.PremiseHazardForm.controls['addressId'].setValue(null);
    this.PremiseHazardForm.controls['addressId'].updateValueAndValidity();
    this.cdr.detectChanges();
  }

  public onGeocodeAddressCleared(): void {
    this.isCreatingAddress = false;
    this.selectedAddressCandidate = undefined;
    this.PremiseHazardForm.controls['addressId'].addValidators([Validators.required]);
    this.PremiseHazardForm.controls['addressId'].setValue(null);
    this.PremiseHazardForm.controls['addressId'].updateValueAndValidity();
    this.cdr.detectChanges();
  }

  public clearAddressClicked(): void {
    this.confirmationService.confirm({
      header: this.transloco.translate('PremiseHazard.modal.clearAddress.header'),
      message: this.transloco.translate('PremiseHazard.modal.clearAddress.content'),
      acceptIcon: 'ic_trash',
      rejectIcon: 'ic_remove',
      acceptLabel: this.transloco.translate('PremiseHazard.modal.clearAddress.confirmButton'),
      rejectLabel: this.transloco.translate('Buttons.cancel'),
      accept: () => this.onGeocodeAddressCleared(),
    });
  }

  public onContactsChanged(contacts: ContactInputModel[]): void {}

  public onPremiseHazardTypeSelected(event: any): void {
    if (!event) {
      return;
    }

    if (!event.value?.premiseHazardType) {
      return;
    }

    const type = event.value.premiseHazardType;
    if (type.id === this.EmptyId) {
      this.updateSelectedType(undefined);
      return;
    }
    this.updateSelectedType(type.id);
    this.cdr.detectChanges();
  }

  public showTypeDropdown(): void {
    this.PremiseHazardTypeFilteredOptions = [...this.PremiseHazardTypeOptions];
    this.PremiseHazardTypeAutocomplete.show();
  }

  public onPremiseHazardTypeInputCleared(): void {
    this.PremiseHazardTypeOptions.forEach(o => (o.isSelected = false));
    this.SelectedTypeOption = null;
    this.updateSelectedType(undefined);
    this.cdr.detectChanges();
  }

  public onExistingAddressSelected(event: any): void {
    if (!event?.value?.address) {
      return;
    }

    const address = event.value.address;
    this.updateSelectedAddress(address.id);
    this.cdr.detectChanges();
  }

  public onAddressInputCleared(): void {
    this.SelectedAddressOption = null;
    this.updateSelectedAddress(undefined);
    this.cdr.detectChanges();
  }

  public onTypeInputChanged(event: any): void {
    const text = (event?.query ?? event).trim();
    this.PremiseHazardTypeFilteredOptions = this.PremiseHazardTypeOptions.filter(
      (item) => !text || item['displayValue']?.toLowerCase().includes(text.toLowerCase()),
    );
    this.PremiseHazardTypeOptions.forEach((item, _idx) => {
      if (item.isSelected) {
        if (text.localeCompare(item.value, undefined, { caseFirst: 'lower' }) !== 0) {
          item.isSelected = false;
          this.updateSelectedType(undefined);
        }
      }
    });
  }

  public onAddressInputChanged(event: any): void {
    const text = (event?.query ?? event).trim();
    this.AddressFilteredOptions = this.AddressOptions.filter(
      (item) => !text || item['displayValue']?.toLowerCase().includes(text.toLowerCase()),
    );
    this.AddressOptions.forEach((item, _idx) => {
      if (item.isSelected) {
        if (text.localeCompare(item.value, undefined, { caseFirst: 'lower' }) !== 0) {
          item.isSelected = false;
          this.updateSelectedAddress(undefined);
        }
      }
    });
  }

  private loadTranslations(): void {
    this.transloco
      .selectTranslate('PremiseHazard.error.incomplete')
      .pipe(take(1))
      .subscribe((res) => (this.formIncompleteError = res));
    this.transloco
      .selectTranslate('PremiseHazard.Item.modal.doNotClose')
      .pipe(take(1))
      .subscribe((res) => (this.doNotCloseMessage = res));
    this.transloco
      .selectTranslate('PremiseHazard.Item.modal.saving')
      .pipe(take(1))
      .subscribe((res) => (this.savingPremiseHazardModalTitle = res));
    this.transloco
      .selectTranslate('PremiseHazard.Item.toast.createSuccess')
      .pipe(take(1))
      .subscribe((res) => (this.saveSuccessMessage = res));
    this.transloco
      .selectTranslate('PremiseHazard.Item.toast.updateSuccess')
      .pipe(take(1))
      .subscribe((res) => (this.updateSuccessMessage = res));
  }

  private setupAutocompletes(): void {
    let typeIdx: number = -1;
    let addressIdx: number = -1;

    let selectedTypeId: string | undefined = undefined;
    let selectedAddressId: string | undefined = undefined;

    if (this.IsUpdating) {
      if (this.SelectedPremiseHazard!.type?.id) {
        selectedTypeId = this.SelectedPremiseHazard!.type.id;
      }
      if (this.SelectedPremiseHazard!.address?.id) {
        selectedAddressId = this.SelectedPremiseHazard!.address.id;
      }
    }

    for (let i = 0; i < this.PremiseHazardTypes.length; i++) {
      const type = this.PremiseHazardTypes[i];
      const isSelected = type.id === selectedTypeId;
      this.PremiseHazardTypeOptions.push({
        id: type.id,
        name: type.code,
        value: type.code,
        displayValue: type.code,
        premiseHazardType: type,
        fuzzySearchSimilarityScore: 3,
      });
      if (isSelected) {
        typeIdx = i;
      }
    }

    for (let i = 0; i < this.Addresses.length; i++) {
      const address = this.Addresses[i];
      const formattedAddress = this.formatAddress(address);
      const isSelected = address.id === selectedAddressId;

      this.AddressOptions.push({
        id: address.id,
        name: formattedAddress,
        value: formattedAddress,
        displayValue: formattedAddress,
        address: address,
        fuzzySearchSimilarityScore: 3,
      });

      if (isSelected) {
        addressIdx = i;
      }
    }

    if (typeIdx !== -1) {
      this.PremiseHazardTypeOptions[typeIdx].isSelected = true;
      this.PremiseHazardForm.controls['premiseHazardTypeId'].setValue(this.PremiseHazardTypes[typeIdx].id);
      this.SelectedTypeOption = this.PremiseHazardTypeOptions[typeIdx];
    }
    if (addressIdx !== -1) {
      this.AddressOptions[addressIdx].isSelected = true;
      this.PremiseHazardForm.controls['addressId'].setValue(this.Addresses[addressIdx].id);
      this.SelectedAddressOption = this.AddressOptions[addressIdx];
    }

    this.IsAutocompleteLoading = false;
    this.PremiseHazardTypeFilteredOptions = [...this.PremiseHazardTypeOptions];
    this.AddressFilteredOptions = [...this.AddressOptions];
  }

  protected override handleSubmitResponse(response$: Observable<ResponseItem<PremiseHazard>>, successMessage: string): void {
    if (!this.IsQuickEdit) {
      super.handleSubmitResponse(response$, successMessage);
      return;
    }
    response$.pipe(take(1)).subscribe({
      next: (response) => {
        if (response.Item) {
          this.toastServiceManager.success(successMessage);
        }
        this.quickEditEvent.emit({ itemCreated: true, itemId: response.Item?.id });
      },
      error: () => {
        this.IsSubmitting = false;
      },
    });
  }

  private submitPremiseHazard(premiseHazardInput: PremiseHazardRecordInputModel): void {
    const loadingRef = this.dialogService.open(LoadingDialogComponent, {
      closable: false,
      modal: true,
      dismissableMask: false,
      closeOnEscape: false,
      showHeader: false,
      data: { title: this.savingPremiseHazardModalTitle, message: this.doNotCloseMessage } as LoadingDialogData,
    });

    const response$ = this.IsCreating
      ? this.commandService.createPremiseHazardRecord(premiseHazardInput)
      : this.commandService.updatePremiseHazardRecord(this.SelectedPremiseHazard!.id, premiseHazardInput, this.ETag!);

    this.handleSubmitResponse(
      response$.pipe(finalize(() => loadingRef.close())),
      this.IsCreating ? this.saveSuccessMessage : this.updateSuccessMessage,
    );
  }

  private createPremiseHazardForm(): void {
    this.PremiseHazardForm = new FormGroup({
      recordTitle: new FormControl<string>(this.SelectedPremiseHazard?.recordTitle ?? '', [
        Validators.required,
        Validators.minLength(1),
        Validators.maxLength(255),
      ]),
      premiseHazardTypeId: new FormControl<string | undefined>(this.SelectedPremiseHazard?.type?.id, [Validators.required]),
      addressId: new FormControl<string | undefined>(this.SelectedPremiseHazard?.address?.id, [Validators.required]),
      ownerName: new FormControl<string | undefined>(this.SelectedPremiseHazard?.ownerName, [
        Validators.minLength(1),
        Validators.maxLength(255),
      ]),
      ownerPhone: new FormControl<string | undefined>(this.SelectedPremiseHazard?.ownerPhone, [
        Validators.minLength(1),
        Validators.maxLength(20),
      ]),
      ownerAddress: new FormControl<string | undefined>(this.SelectedPremiseHazard?.ownerAddress, [
        Validators.minLength(1),
        Validators.maxLength(255),
      ]),
      comments: new FormControl<string>(this.SelectedPremiseHazard?.comments ?? '', [
        Validators.minLength(0),
        Validators.maxLength(4096),
      ]),
      authorizingPersonnel: new FormControl<string>(this.SelectedPremiseHazard?.authorizingPersonnel ?? '', [
        Validators.minLength(1),
        Validators.maxLength(255),
      ]),
      purgeDate: new FormControl<Date>(this.SelectedPremiseHazard?.purgeDate ?? new Date()),
      purgeType: new FormControl<string>(this.SelectedPremiseHazard?.purgeType ?? ''),
      isTemporary: new FormControl<boolean>(this.SelectedPremiseHazard?.isTemporary ?? false),
      isReviewRequired: new FormControl<boolean>(this.SelectedPremiseHazard?.isReviewRequired ?? true),
      priority: new FormControl<string>(
        this.SelectedPremiseHazard?.priority?.toString().toLowerCase() ??
          this.PRIORITY_TYPES[PremiseHazardPriorityType.unset].toString(),
        [Validators.required],
      ),
    });
  }

  private formatAddress(address: Address): string {
    let formattedAddress: string = '';

    if (address.commonPlace) {
      formattedAddress = address.commonPlace;
    }
    formattedAddress = this.appendSegment(formattedAddress, address.houseAddress, true);
    formattedAddress = this.appendSegment(formattedAddress, address.city, true);
    formattedAddress = this.appendSegment(formattedAddress, address.state, true);
    formattedAddress = this.appendSegment(formattedAddress, address.postalCode, false);

    return formattedAddress;
  }

  private appendSegment(inputAddress: string, newSegment: string, prependComma: boolean): string {
    if (!newSegment || newSegment.length === 0) {
      return inputAddress;
    }
    let outputAddress: string = inputAddress;

    if (inputAddress.length > 0) {
      if (prependComma) {
        outputAddress += ',';
      }
      outputAddress += ' ';
    }
    outputAddress += newSegment;

    return outputAddress;
  }

  private updateSelectedType(typeId?: string): void {
    this.PremiseHazardForm.controls['premiseHazardTypeId'].setValue(typeId);
    this.PremiseHazardForm.controls['premiseHazardTypeId'].updateValueAndValidity();
  }

  private updateSelectedAddress(addressId?: string): void {
    this.PremiseHazardForm.controls['addressId'].setValue(addressId);
    this.PremiseHazardForm.controls['addressId'].updateValueAndValidity();
  }
}
